import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const INPUT = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const LABEL = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400";
const cname = (c) => c.brand_name || c.preferred_name || c.legal_name || c.full_legal_name || c.customer_id;

export default function AIInquiryAssistant({ onClose, onSaved }) {
  const [step, setStep] = useState("input");
  const [rawText, setRawText] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerConfirmed, setCustomerConfirmed] = useState(false);
  const [noDateConfirmed, setNoDateConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);

  const analyze = async () => {
    if (rawText.trim().length < 10) return toast.error("Paste the customer request first (min 10 characters)");
    setAnalyzing(true);
    try {
      const r = await api.post("/ai/analyze-request", { raw_text: rawText.trim() });
      setResult(r.data);
      const e = r.data.extracted_data;
      setForm({
        trip_title: e.trip_title || "",
        departure_date: e.travel_date?.departure_date || "",
        duration_days: e.duration_days ?? "",
        pax_count: e.pax_count ?? "",
        destinations: (e.destinations || []).join(", "),
      });
      const cr = await api.get("/customers", { params: { status: "active" } });
      setCustomers(cr.data);
      const cand = r.data.customer_candidate;
      if (cand.match_type === "exact" && cand.customer_id) {
        const hit = cr.data.find((c) => c.customer_id === cand.customer_id);
        if (hit) setSelectedCustomer(hit.id);
      }
      setStep("review");
    } catch (e) { toast.error(apiError(e)); } finally { setAnalyzing(false); }
  };

  const td = result?.extracted_data?.travel_date || {};
  const dateNeedsResolution = !!result && !form?.departure_date;
  const canSave = !!selectedCustomer && customerConfirmed && (!!form?.departure_date || noDateConfirmed);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomer,
        trip_type: form.trip_title || undefined,
        destinations: form.destinations ? form.destinations.split(",").map((s) => s.trim()).filter(Boolean) : [],
        num_travelers: Number(form.pax_count) || 1,
        notes: `AI-drafted from customer request.\n\nOriginal request:\n${rawText.trim()}`,
      };
      if (form.departure_date) payload.travel_start_date = form.departure_date;
      const r = await api.post("/inquiries", payload);
      toast.success(`Inquiry ${r.data.inquiry_number} created`);
      onSaved(r.data.id);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const candidate = result?.customer_candidate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="ai-review-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-sky-600 dark:text-ink-gold" />
            <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">AI Requirement Extractor — Review Center</h3>
          </div>
          <button onClick={onClose} data-testid="ai-modal-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>

        {step === "input" ? (
          <div className="space-y-4 overflow-y-auto px-6 py-5">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Paste the customer message (email / WhatsApp). The AI structures it into a draft — nothing is saved until you review and confirm.
            </p>
            <textarea rows={10} value={rawText} onChange={(e) => setRawText(e.target.value)}
              placeholder={"Hi, we are a family of 4 looking for a Bali trip in August…"}
              data-testid="ai-raw-text-input" className={`${INPUT} font-mono-tf text-xs`} />
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
              <button onClick={analyze} disabled={analyzing} data-testid="ai-analyze-button"
                className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
                {analyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Analyze request
              </button>
            </div>
          </div>
        ) : (
          <div className="grid flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-2">
            {/* Original request */}
            <div className="overflow-y-auto border-b border-slate-100 px-6 py-5 dark:border-ink-line lg:border-b-0 lg:border-r">
              <label className={LABEL}>Original request</label>
              <pre className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono-tf text-xs text-slate-700 dark:border-ink-line dark:bg-ink-panel/40 dark:text-slate-300" data-testid="ai-original-text">{rawText.trim()}</pre>
              {result.missing_fields?.length > 0 && (
                <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-500/30 dark:bg-rose-500/10" data-testid="ai-missing-fields">
                  <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-rose-600 dark:text-rose-400">
                    <AlertTriangle className="h-3.5 w-3.5" /> Missing information
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {result.missing_fields.map((f) => (
                      <span key={f} className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-500/20 dark:text-rose-300" data-testid={`ai-missing-${f}`}>{f}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Extracted draft */}
            <div className="space-y-4 overflow-y-auto px-6 py-5">
              <div>
                <label className={LABEL}>Trip title</label>
                <input value={form.trip_title} onChange={set("trip_title")} data-testid="ai-field-trip-title" className={INPUT} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={LABEL}>Departure date</label>
                  <input type="date" value={form.departure_date} onChange={set("departure_date")} data-testid="ai-field-departure-date"
                    className={`${INPUT} ${dateNeedsResolution ? "border-rose-300 dark:border-rose-500/50" : ""}`} />
                </div>
                <div>
                  <label className={LABEL}>Duration (days)</label>
                  <input type="number" min="1" value={form.duration_days} onChange={set("duration_days")} data-testid="ai-field-duration" className={INPUT} />
                </div>
                <div>
                  <label className={LABEL}>Travelers</label>
                  <input type="number" min="1" value={form.pax_count} onChange={set("pax_count")} data-testid="ai-field-pax"
                    className={`${INPUT} ${result.missing_fields?.includes("pax_count") ? "border-rose-300 dark:border-rose-500/50" : ""}`} />
                </div>
              </div>
              {dateNeedsResolution && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300" data-testid="ai-date-warning">
                  <div className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Ambiguous or missing travel date</div>
                  <p className="mt-1">The request said: <span className="font-mono-tf font-semibold">"{td.date_text || "no date mentioned"}"</span>. No exact date was invented — pick a date above, or explicitly proceed without one.</p>
                  <label className="mt-2 flex items-center gap-2 font-medium">
                    <input type="checkbox" checked={noDateConfirmed} onChange={(e) => setNoDateConfirmed(e.target.checked)} data-testid="ai-no-date-confirm" className="h-4 w-4 rounded border-slate-300" />
                    Proceed without a fixed departure date
                  </label>
                </div>
              )}
              <div>
                <label className={LABEL}>Destinations (comma-separated)</label>
                <input value={form.destinations} onChange={set("destinations")} data-testid="ai-field-destinations"
                  className={`${INPUT} ${result.missing_fields?.includes("destinations") ? "border-rose-300 dark:border-rose-500/50" : ""}`} />
              </div>

              <div className={`rounded-lg border p-3 ${candidate?.match_type === "exact" ? "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/10" : "border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/10"}`} data-testid="ai-customer-candidate">
                {candidate?.match_type === "exact" ? (
                  <div className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-300">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <div className="font-semibold" data-testid="ai-customer-match-label">Exact customer candidate: {candidate.customer_id}</div>
                      <div className="mt-0.5">{candidate.match_reason} — verify below before saving.</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 text-xs text-rose-700 dark:text-rose-300" data-testid="ai-customer-no-match">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div><span className="font-semibold">No exact customer match.</span> {candidate?.match_reason}. Select the customer manually — no new customer is created automatically.</div>
                  </div>
                )}
                <div className="mt-3">
                  <label className={LABEL}>Customer *</label>
                  <select value={selectedCustomer} onChange={(e) => { setSelectedCustomer(e.target.value); setCustomerConfirmed(false); }} data-testid="ai-customer-select" className={INPUT}>
                    <option value="">Select a customer…</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{`${c.customer_id} · ${cname(c)} (${c.customer_type})`}</option>)}
                  </select>
                </div>
                <label className="mt-2 flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={customerConfirmed} disabled={!selectedCustomer} onChange={(e) => setCustomerConfirmed(e.target.checked)} data-testid="ai-customer-confirm" className="h-4 w-4 rounded border-slate-300" />
                  I confirm this is the correct customer
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <button onClick={() => setStep("input")} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Back</button>
                <button onClick={save} disabled={!canSave || saving} data-testid="ai-confirm-save-button"
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Confirm and Save
                </button>
              </div>
              {!canSave && <p className="text-right text-[11px] text-slate-400" data-testid="ai-save-hint">Select and confirm the customer{dateNeedsResolution && ", and resolve the travel date"} to enable saving.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
